import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { InstagramLogo, LinkedinLogo, TiktokLogo, YoutubeLogo } from '@phosphor-icons/react'
import api from '../services/api'
import { useBilling, TIER_CONFIG } from '../contexts/BillingContext'

/**
 * Qué fuentes alimentan el análisis y cuáles podrían hacerlo.
 *
 * Una pestaña vacía no dice nada; esta tabla sí: qué está dando datos, qué
 * falta por conectar y qué todavía no existe. Lo que no se puede conectar hoy
 * se dice, pero no se disfraza de botón.
 */

const SOURCE_STYLE = {
  youtube: { label: 'YouTube', color: '#FF0000', Icon: YoutubeLogo },
  instagram: { label: 'Instagram', color: '#E1306C', Icon: InstagramLogo },
  linkedin: { label: 'LinkedIn', color: '#0077B5', Icon: LinkedinLogo },
  tiktok: { label: 'TikTok', color: '#69C9D0', Icon: TiktokLogo },
}

function SourceName({ source, title, detail }) {
  const { color, Icon } = SOURCE_STYLE[source]
  return (
    <div className="flex items-center gap-3 min-w-0">
      <Icon size={16} style={{ color }} weight="fill" className="shrink-0" />
      <div className="min-w-0">
        <p
          className="text-ds-text text-[13px] font-medium truncate"
          style={{ fontFamily: 'Barlow, sans-serif' }}
        >
          {title}
        </p>
        {detail && (
          <p className="text-[#555555] text-[11px]" style={{ fontFamily: 'Barlow, sans-serif' }}>
            {detail}
          </p>
        )}
      </div>
    </div>
  )
}

function AvailableCard({ source, title, badge, description, action }) {
  const { label, color, Icon } = SOURCE_STYLE[source]
  return (
    <div className="card p-5 flex flex-col gap-3">
      <Icon size={22} style={{ color }} weight="fill" />
      <div className="flex items-center gap-2.5">
        <h3 className="type-h3 text-ds-text text-[15px]">{title || label}</h3>
        {badge && <span className="badge-muted">{badge}</span>}
      </div>
      <p
        className="text-ds-muted text-xs leading-relaxed flex-1"
        style={{ fontFamily: 'Barlow, sans-serif' }}
      >
        {description}
      </p>
      {action}
    </div>
  )
}

export default function SourcesPanel({ projects = [], onOpenTab }) {
  const { plan } = useBilling()
  const [status, setStatus] = useState({ youtube: null, instagram: null, linkedin: null })

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const [youtube, instagram, linkedin] = await Promise.all(
        ['/youtube/status', '/instagram/status', '/linkedin/status'].map(path =>
          api
            .get(path)
            .then(res => res.data)
            .catch(() => ({ connected: false }))
        )
      )
      if (!cancelled) setStatus({ youtube, instagram, linkedin })
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const days = TIER_CONFIG[plan]?.analytics_days
  const dataWindow = days ? `Últimos ${days} días` : 'Histórico completo'
  const ytLinked = projects.filter(p => p.youtube_video_id).length

  const connected = []
  if (status.youtube?.connected) {
    connected.push({
      key: 'youtube',
      title: status.youtube.channel_name ? `YouTube · ${status.youtube.channel_name}` : 'YouTube',
      detail: `${ytLinked} ${ytLinked === 1 ? 'vídeo vinculado' : 'vídeos vinculados'}`,
      window: dataWindow,
    })
  }
  if (status.instagram?.connected) {
    connected.push({
      key: 'instagram',
      title: status.instagram.username ? `Instagram · @${status.instagram.username}` : 'Instagram',
      detail: status.instagram.account_type
        ? `Cuenta ${status.instagram.account_type.toLowerCase()}`
        : null,
      window: dataWindow,
    })
  }

  return (
    <div className="space-y-8">
      <section className="space-y-3.5">
        <p className="type-label text-ds-muted">Fuentes conectadas</p>
        <div className="border border-ds-border overflow-hidden" style={{ borderRadius: 2 }}>
          <div className="hidden sm:flex items-center px-4 py-3 bg-ds-surface">
            <span className="type-caption text-[#555555] flex-1">Fuente</span>
            <span className="type-caption text-[#555555] w-32">Estado</span>
            <span className="type-caption text-[#555555] w-40">Datos disponibles</span>
          </div>
          {connected.length === 0 ? (
            <div className="px-4 py-5 border-t border-ds-border">
              <p className="text-ds-muted text-sm" style={{ fontFamily: 'Barlow, sans-serif' }}>
                Ninguna todavía. El análisis funciona con tus proyectos, pero con una cuenta
                conectada trae también los números reales.
              </p>
            </div>
          ) : (
            connected.map(row => (
              <div
                key={row.key}
                className="flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-4 border-t border-ds-border"
                data-testid={`source-row-${row.key}`}
              >
                <div className="flex-1 min-w-0">
                  <SourceName source={row.key} title={row.title} detail={row.detail} />
                </div>
                <div className="flex items-center gap-2 w-32">
                  <span
                    className="w-1.5 h-1.5 bg-[#30D158] rounded-full shrink-0"
                    aria-hidden="true"
                  />
                  <span
                    className="text-[#30D158] text-xs"
                    style={{ fontFamily: 'Barlow, sans-serif' }}
                  >
                    Activa
                  </span>
                </div>
                <span
                  className="text-ds-muted text-xs w-40"
                  style={{ fontFamily: 'Barlow, sans-serif' }}
                >
                  {row.window}
                </span>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="space-y-3.5">
        <div className="flex flex-wrap items-center gap-3">
          <p className="type-label text-ds-muted">Fuentes disponibles</p>
          <p className="text-ds-faint text-[11px]" style={{ fontFamily: 'Barlow, sans-serif' }}>
            Conecta una cuenta para ver sus métricas aquí
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {!status.youtube?.connected && (
            <AvailableCard
              source="youtube"
              description="Vistas, retención y suscriptores de tu canal, junto a los proyectos que ya tienes aquí."
              action={
                <Link to="/youtube" className="btn-ghost w-full text-[12px] py-2 no-underline">
                  Conectar
                </Link>
              }
            />
          )}
          {!status.instagram?.connected && (
            <AvailableCard
              source="instagram"
              description="Alcance, guardados y Reels de tu cuenta Business o Creator."
              action={
                <button
                  type="button"
                  onClick={() => onOpenTab?.('instagram')}
                  className="btn-ghost w-full text-[12px] py-2"
                  data-testid="source-connect-instagram"
                >
                  Conectar
                </button>
              }
            />
          )}
          <AvailableCard
            source="linkedin"
            badge="En desarrollo"
            description="Publicar ya funciona hoy. Las métricas de vuelta llegan más adelante."
            action={
              <Link to="/linkedin" className="btn-ghost w-full text-[12px] py-2 no-underline">
                Ver la integración
              </Link>
            }
          />
          <AvailableCard
            source="tiktok"
            badge="En desarrollo"
            description="Visualizaciones y retención de tus vídeos cortos, junto a los de YouTube."
            action={
              <p
                className="text-ds-faint text-[11px] py-2"
                style={{ fontFamily: 'Barlow, sans-serif' }}
              >
                Todavía no se puede conectar
              </p>
            }
          />
          {TIER_CONFIG[plan]?.max_channels === 1 && (
            <AvailableCard
              source="youtube"
              title="Segundo canal"
              description={`Hasta ${TIER_CONFIG.pro.max_channels} canales de YouTube con el plan Pro. Ahora tienes uno.`}
              action={
                <Link to="/pricing" className="btn-accent w-full text-[12px] py-2 no-underline">
                  Actualizar a Pro
                </Link>
              }
            />
          )}
        </div>
      </section>
    </div>
  )
}
